import { useState, useEffect, useRef } from 'react'
import type { MetaFunction, LoaderFunctionArgs } from 'react-router'
import { Link, useLoaderData } from 'react-router'
import { Navbar } from '~/components/navbar'
import { Footer } from '~/components/footer'
import { SearchAutocomplete } from '~/components/search-autocomplete'
import { PartnerLogos } from '~/components/trust-guarantee'
import { StructuredData } from '~/components/structured-data'
import { db } from '@afficheservices/db'
import { serviceCategories, professionalProfiles, professionalCategories, reviews, users, leads } from '@afficheservices/db/schema'
import { isNull, eq, sql, desc, and, gte } from 'drizzle-orm'
import {
  Wrench, Zap, Paintbrush, HardHat, Hammer, Home, Key, SprayCan,
  TreePine, Snowflake, Truck, Scissors, Star, CheckCircle, ArrowRight,
  Shield, Clock, Users, Quote, BadgeCheck, ChevronRight, FileText, MapPin,
} from 'lucide-react'
import { STATIC_PAGE_HEADERS } from '~/lib/cache.server'
import { getVariant } from '~/lib/ab-test'
import { FadeInUp, StaggerChildren } from '~/components/scroll-animations'

export function headers() { return STATIC_PAGE_HEADERS }

export const meta: MetaFunction = () => [
  { title: 'AfficheServices — Trouvez le bon artisan près de chez vous' },
  { name: 'description', content: "Trouvez un artisan vérifié : plomberie, électricité, peinture, rénovation. 110k+ pros SIRET-vérifiés + RGE. 3 devis gratuits sous 48h." },
  { tagName: 'link', rel: 'canonical', href: 'https://www.afficheservices.fr/' },
  { property: 'og:title', content: 'AfficheServices — Trouvez le bon artisan près de chez vous' },
  { property: 'og:description', content: "Comparez les devis de professionnels vérifiés. Service gratuit, réponse sous 48h." },
  { property: 'og:url', content: 'https://www.afficheservices.fr/' },
  { property: 'og:type', content: 'website' },
  { property: 'og:site_name', content: 'AfficheServices' },
  { property: 'og:locale', content: 'fr_FR' },
  { name: 'twitter:title', content: 'AfficheServices — Artisans vérifiés, devis gratuits' },
  { name: 'twitter:description', content: "Comparez les devis de professionnels vérifiés. 100% gratuit." },
  { property: 'og:image', content: 'https://www.afficheservices.fr/og-default.png' },
  { property: 'og:image:width', content: '1200' },
  { property: 'og:image:height', content: '630' },
]

const ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  plomberie: Wrench, electricite: Zap, peinture: Paintbrush, renovation: HardHat,
  menuiserie: Hammer, toiture: Home, serrurerie: Key, nettoyage: SprayCan,
  jardinage: TreePine, climatisation: Snowflake, demenagement: Truck, 'carrelage---sol': Scissors,
}

const ICON_COLORS: Record<string, string> = {
  plomberie: 'bg-blue-100 text-blue-600 group-hover:bg-blue-600',
  electricite: 'bg-amber-100 text-amber-600 group-hover:bg-amber-500',
  peinture: 'bg-pink-100 text-pink-600 group-hover:bg-pink-500',
  renovation: 'bg-orange-100 text-orange-600 group-hover:bg-orange-500',
  menuiserie: 'bg-emerald-100 text-emerald-600 group-hover:bg-emerald-500',
  toiture: 'bg-red-100 text-red-600 group-hover:bg-red-500',
  serrurerie: 'bg-violet-100 text-violet-600 group-hover:bg-violet-500',
  nettoyage: 'bg-cyan-100 text-cyan-600 group-hover:bg-cyan-500',
  jardinage: 'bg-green-100 text-green-600 group-hover:bg-green-500',
  climatisation: 'bg-sky-100 text-sky-600 group-hover:bg-sky-500',
  demenagement: 'bg-indigo-100 text-indigo-600 group-hover:bg-indigo-500',
  'carrelage-sol': 'bg-stone-100 text-stone-600 group-hover:bg-stone-500',
}

// Real service prices (client-facing, NOT lead costs)
const SERVICE_PRICES: Record<string, string> = {
  plomberie: '80 — 500',
  electricite: '150 — 1 500',
  peinture: '20 — 45/m²',
  renovation: '5 000 — 25 000',
  menuiserie: '200 — 3 000',
  'carrelage-sol': '25 — 70/m²',
  toiture: '500 — 5 000',
  serrurerie: '80 — 400',
  nettoyage: '20 — 50/h',
  jardinage: '30 — 80/h',
  climatisation: '500 — 3 000',
  demenagement: '300 — 3 000',
}

// Unsplash images for hero and project cards (free to use)
const _HERO_IMAGES = [
  'https://images.unsplash.com/photo-1621905252507-b35492cc74b4?w=800&q=80', // electrician at work
  'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=800&q=80', // modern house interior
  'https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?w=800&q=80', // kitchen renovation
  'https://images.unsplash.com/photo-1600573472592-401b489a3cdc?w=800&q=80', // bathroom renovation
]

const _PROJECT_IMAGES: Record<string, string> = {
  plomberie: 'https://images.unsplash.com/photo-1585704032915-c3400ca199e7?w=600&q=80',
  electricite: 'https://images.unsplash.com/photo-1621905252507-b35492cc74b4?w=600&q=80',
  peinture: 'https://images.unsplash.com/photo-1562259949-e8e7689d7828?w=600&q=80',
  renovation: 'https://images.unsplash.com/photo-1503387762-592deb58ef4e?w=600&q=80',
  menuiserie: 'https://images.unsplash.com/photo-1600585152220-90363fe7e115?w=600&q=80',
  toiture: 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=600&q=80',
  nettoyage: 'https://images.unsplash.com/photo-1628177142898-93e36e4e3a50?w=600&q=80',
  jardinage: 'https://images.unsplash.com/photo-1416879595882-3373a0480b5b?w=600&q=80',
  serrurerie: 'https://images.unsplash.com/photo-1558317374-067fb5f30001?w=600&q=80',
  climatisation: 'https://images.unsplash.com/photo-1631545806609-05fade2b5267?w=600&q=80',
}

const POPULAR_SEARCHES = [
  { label: 'Plombier Nice', href: '/prix/plombier-nice' },
  { label: 'Électricien Antibes', href: '/prix/electricien-antibes' },
  { label: 'Peintre Cannes', href: '/prix/peintre-cannes' },
  { label: 'Climatisation Mougins', href: '/prix/climaticien-mougins' },
  { label: 'Rénovation Grasse', href: '/prix/artisan-renovation-grasse' },
  { label: 'Plombier Menton', href: '/prix/plombier-menton' },
  { label: 'Serrurier Nice', href: '/prix/serrurier-nice' },
  { label: 'Couvreur Antibes', href: '/prix/couvreur-antibes' },
]

// Homepage entry links into the /problemes mesh. GSC audit 2026-07 found the
// homepage + all hubs linked ZERO /problemes URLs, so the ~13k problemes
// whitelist pages had no navigational entry point for Googlebot (sitemap-only
// = deprioritised crawl). These link to the category hubs (/problemes/{slug}),
// which fan out to the city pages.
const POPULAR_PROBLEMS = [
  { slug: 'fuite-eau', label: "Fuite d'eau" },
  { slug: 'canalisation-bouchee', label: 'Canalisation bouchée' },
  { slug: 'panne-chaudiere', label: 'Panne de chaudière' },
  { slug: 'serrure-bloquee', label: 'Serrure bloquée' },
  { slug: 'panne-electrique', label: 'Panne électrique' },
  { slug: 'infiltration-toiture', label: 'Infiltration de toiture' },
  { slug: 'wc-bouche', label: 'WC bouché' },
  { slug: 'disjoncteur-qui-saute', label: 'Disjoncteur qui saute' },
  { slug: 'porte-claquee', label: 'Porte claquée' },
  { slug: 'radiateur-froid', label: 'Radiateur froid' },
  { slug: 'fissure-mur', label: 'Fissure dans un mur' },
  { slug: 'humidite', label: "Problème d'humidité" },
]

const CITY_CHIPS = ['Nice', 'Antibes', 'Cannes', 'Grasse', 'Menton', 'Mougins', 'Cagnes-sur-Mer']

const REALISATION_CARDS = [
  { type: 'Rénovation salle de bain', city: 'Nice', budget: '6 500 €', description: 'Réfection complète : douche italienne, double vasque, carrelage grand format. Travaux réalisés en 10 jours.', href: '/prix/plombier-nice' },
  { type: 'Installation climatisation', city: 'Cannes', budget: '2 800 €', description: 'Pose d\'une clim réversible bi-split dans un T3. Économie de 40 % sur le chauffage hivernal.', href: '/prix/climaticien-cannes' },
  { type: 'Mise aux normes électrique', city: 'Antibes', budget: '4 200 €', description: 'Tableau électrique neuf, prises aux normes NF C 15-100, mise à la terre. Appartement 75 m².', href: '/prix/electricien-antibes' },
]

export async function loader({ request }: LoaderFunctionArgs) {
  const heroVariant = getVariant(request, 'homepage-hero')
  const todayStart = new Date()
  todayStart.setHours(0, 0, 0, 0)

  // Parallelize 8 independent queries — was sequential, ~650ms TTFB.
  // Promise.all collapses to ~max(query) which is the slowest single query.
  const [
    categories,
    reviewRows,
    recentLeads,
    proCounts,
    todayLeadsRow,
    totalLeadsRow,
    totalProsRow,
    citiesRow,
    aggRow,
  ] = await Promise.all([
    db
      .select({ id: serviceCategories.id, name: serviceCategories.nameFr, slug: serviceCategories.slug, leadPriceMin: serviceCategories.leadPriceMin, leadPriceMax: serviceCategories.leadPriceMax })
      .from(serviceCategories)
      .where(isNull(serviceCategories.parentId))
      .orderBy(serviceCategories.displayOrder),
    db
      .select({
        id: reviews.id,
        name: users.firstName,
        rating: reviews.overallRating,
        text: reviews.comment,
        city: professionalProfiles.city,
        service: serviceCategories.nameFr,
      })
      .from(reviews)
      .leftJoin(users, eq(users.id, reviews.reviewerId))
      .leftJoin(professionalProfiles, eq(professionalProfiles.id, reviews.professionalId))
      .leftJoin(professionalCategories, eq(professionalCategories.professionalId, professionalProfiles.id))
      .leftJoin(serviceCategories, eq(serviceCategories.id, professionalCategories.categoryId))
      .where(and(eq(reviews.status, 'approved'), sql`${reviews.overallRating} >= 4`))
      .orderBy(desc(reviews.createdAt))
      .limit(20),
    db
      .select({
        city: leads.city,
        categoryName: serviceCategories.nameFr,
        urgency: leads.urgency,
        createdAt: leads.createdAt,
      })
      .from(leads)
      .leftJoin(serviceCategories, eq(serviceCategories.id, leads.categoryId))
      .where(eq(leads.status, 'submitted'))
      .orderBy(desc(leads.createdAt))
      .limit(6),
    db
      .select({
        categoryId: professionalCategories.categoryId,
        count: sql<number>`count(DISTINCT ${professionalCategories.professionalId})::int`,
      })
      .from(professionalCategories)
      .groupBy(professionalCategories.categoryId),
    db.select({ count: sql<number>`count(*)::int` }).from(leads).where(gte(leads.createdAt, todayStart)).then((r) => r[0]),
    db.select({ count: sql<number>`count(*)::int` }).from(leads).then((r) => r[0]),
    db.select({ count: sql<number>`count(*)::int` }).from(professionalProfiles).then((r) => r[0]),
    db.select({ count: sql<number>`count(DISTINCT ${professionalProfiles.city})::int` }).from(professionalProfiles).then((r) => r[0]),
    db
      .select({
        avg: sql<number>`COALESCE(AVG(${reviews.overallRating})::numeric(3,1), 0)`,
        count: sql<number>`count(*)::int`,
      })
      .from(reviews)
      .where(eq(reviews.status, 'approved'))
      .then((r) => r[0]),
  ])

  const seenReviewIds = new Set<string>()
  const realTestimonials: Array<{ name: string; rating: number; text: string | null; city: string | null; service: string | null }> = []
  for (const row of reviewRows) {
    if (seenReviewIds.has(row.id)) continue
    seenReviewIds.add(row.id)
    realTestimonials.push({
      name: row.name ?? 'Client',
      rating: row.rating,
      text: row.text,
      city: row.city,
      service: row.service,
    })
    if (realTestimonials.length >= 3) break
  }

  // Use ONLY real reviews — fallback to fake testimonials was a trust killer
  // (audit #5): visitors can't tell apart "Marie L. — Plomberie" stock copy
  // from genuine feedback, and once they spot one fake the whole site loses
  // credibility. If we have no approved reviews yet, the testimonial section
  // won't render at all (the page handles `testimonials.length === 0`).
  const testimonials = realTestimonials.map((t) => ({
    name: `${t.name}.`,
    city: t.city,
    rating: t.rating,
    text: t.text,
    service: t.service,
  }))

  const proCountMap: Record<string, number> = {}
  for (const pc of proCounts) {
    proCountMap[pc.categoryId] = pc.count
  }

  const todayLeadCount = todayLeadsRow?.count ?? 0
  const totalLeadCount = totalLeadsRow?.count ?? 0
  const totalProsCount = totalProsRow?.count ?? 0
  const totalProsDisplay = Math.floor(totalProsCount / 100) * 100
  const citiesCount = citiesRow?.count ?? 0
  const avgRating = Number(aggRow?.avg ?? 0)
  const totalReviews = Number(aggRow?.count ?? 0)

  // Compute "Mis à jour en {month} {year}" on the server so SSR and CSR
  // never disagree (audit #17). new Date() in render caused hydration drift
  // around midnight or on slow client clocks.
  const lastUpdatedLabel = new Date().toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })

  return { categories, testimonials, recentLeads, proCountMap, heroVariant, todayLeadCount, totalLeadCount, totalProsCount, totalProsDisplay, citiesCount, lastUpdatedLabel, avgRating, totalReviews }
}

// ─── Animated Number (scroll-triggered count up) ─────────────
function AnimatedNumber({ value, suffix = '' }: { value: number; suffix?: string }) {
  const [display, setDisplay] = useState(0)
  const ref = useRef<HTMLSpanElement>(null)
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        let start = 0
        const step = Math.ceil(value / 30)
        const timer = setInterval(() => {
          start += step
          if (start >= value) { setDisplay(value); clearInterval(timer) }
          else setDisplay(start)
        }, 30)
        observer.disconnect()
      }
    })
    if (ref.current) observer.observe(ref.current)
    return () => observer.disconnect()
  }, [value])
  return <span ref={ref}>{display.toLocaleString('fr-FR')}{suffix}</span>
}

// ─── Quick Price Estimator (homepage) ─────────────────────────

const QUICK_PRICES = [
  { slug: 'plomberie', name: 'Plomberie', emoji: '🔧', unit: 'intervention', min: 80, max: 250 },
  { slug: 'electricite', name: 'Électricité', emoji: '⚡', unit: 'point', min: 60, max: 150 },
  { slug: 'peinture', name: 'Peinture', emoji: '🎨', unit: 'm²', min: 20, max: 45 },
  { slug: 'renovation', name: 'Rénovation SDB', emoji: '🛁', unit: 'm²', min: 800, max: 1500 },
  { slug: 'carrelage-sol', name: 'Carrelage', emoji: '🔲', unit: 'm²', min: 25, max: 70 },
  { slug: 'toiture', name: 'Toiture', emoji: '🏠', unit: 'm²', min: 40, max: 120 },
  { slug: 'climatisation', name: 'Climatisation', emoji: '❄️', unit: 'unité', min: 800, max: 2000 },
  { slug: 'isolation', name: 'Isolation', emoji: '🧱', unit: 'm²', min: 30, max: 80 },
]

function QuickPriceEstimator() {
  const [selected, setSelected] = useState<number | null>(null)
  const [qty, setQty] = useState('')
  const item = selected !== null ? QUICK_PRICES[selected] : null
  const qtyNum = parseFloat(qty) || 0
  const priceMin = item ? Math.round(item.min * (qtyNum || 1)) : 0
  const priceMax = item ? Math.round(item.max * (qtyNum || 1)) : 0

  return (
    <section className="border-b border-slate-100 bg-gradient-to-b from-white to-slate-50 py-10">
      <div className="mx-auto max-w-4xl px-4">
        <FadeInUp>
          <div className="text-center">
            <h2 className="text-xl font-bold text-slate-900 sm:text-2xl">Estimez le prix de vos travaux</h2>
            <p className="mt-1 text-sm text-slate-500">Sélectionnez un service et obtenez une estimation instantanée</p>
          </div>

          {/* Service pills */}
          <div className="mt-6 flex flex-wrap justify-center gap-2">
            {QUICK_PRICES.map((s, i) => (
              <button
                key={s.slug}
                onClick={() => { setSelected(i); setQty('') }}
                className={`rounded-full border px-4 py-2 text-sm font-medium transition-all ${
                  selected === i
                    ? 'border-primary bg-primary text-white shadow-md shadow-primary/20'
                    : 'border-slate-200 bg-white text-slate-600 hover:border-primary/40 hover:text-primary'
                }`}
              >
                {s.emoji} {s.name}
              </button>
            ))}
          </div>

          {/* Calculator */}
          {item && (
            <div className="mx-auto mt-6 max-w-md animate-fade-in">
              <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                {item.unit !== 'intervention' && item.unit !== 'unité' && (
                  <div className="flex items-center gap-3">
                    <input
                      type="number" min="1" value={qty}
                      onChange={(e) => setQty(e.target.value)}
                      placeholder={`Surface (${item.unit})`}
                      className="w-32 rounded-xl border border-slate-200 px-4 py-3 text-center text-lg font-medium text-slate-900 placeholder:text-slate-400 focus:border-primary focus:outline-none"
                    />
                    <span className="text-sm text-slate-400">{item.unit}</span>
                  </div>
                )}

                {/* Price display */}
                <div className="mt-4 rounded-xl bg-slate-50 p-4 text-center">
                  <p className="text-xs text-slate-400">Estimation {qtyNum > 1 ? `pour ${qtyNum} ${item.unit}` : 'unitaire'}</p>
                  <p className="mt-1 text-3xl font-bold text-slate-900">
                    {priceMin.toLocaleString('fr-FR')} — {priceMax.toLocaleString('fr-FR')} <span className="text-lg">€</span>
                  </p>
                  <p className="mt-1 text-[10px] text-slate-400">TTC · Prix moyens constatés 2026</p>
                </div>

                <Link
                  to={`/devis-gratuit?service=${item.slug}`}
                  className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-primary py-3 text-sm font-bold text-white shadow-md shadow-primary/20 hover:bg-primary/90 transition-all"
                >
                  Obtenir un prix exact — Gratuit <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          )}
        </FadeInUp>
      </div>
    </section>
  )
}

export default function Index() {
  const { categories, testimonials, recentLeads, todayLeadCount, totalLeadCount, totalProsDisplay, citiesCount, lastUpdatedLabel, avgRating, totalReviews } = useLoaderData<typeof loader>()
  const [userCity, setUserCity] = useState<string | null>(null)
  const [savedDevis, setSavedDevis] = useState<string | null>(null)
  const [showBanner, setShowBanner] = useState(true)

  // Check for abandoned devis flow
  useEffect(() => {
    try {
      const saved = sessionStorage.getItem('devis_progress')
      if (saved) setSavedDevis(saved)
    } catch {}
  }, [])

  // Auto-detect city on mount (non-blocking)
  useEffect(() => {
    // Check if we already have a saved city
    try {
      const saved = localStorage.getItem('afficheservices_user_city')
      if (saved) {
        setUserCity(saved)
        return
      }
    } catch { /* ignore */ }

    // Try geolocation API silently (only if permission already granted)
    if (navigator.permissions) {
      navigator.permissions.query({ name: 'geolocation' }).then((result) => {
        if (result.state === 'granted') {
          navigator.geolocation.getCurrentPosition(
            async (pos) => {
              try {
                const res = await fetch(
                  `https://api-adresse.data.gouv.fr/reverse/?lon=${pos.coords.longitude}&lat=${pos.coords.latitude}&limit=1`,
                )
                const data = await res.json()
                const city = data.features?.[0]?.properties?.city
                if (city) {
                  setUserCity(city)
                  localStorage.setItem('afficheservices_user_city', city)
                }
              } catch { /* ignore */ }
            },
            () => {},
            { enableHighAccuracy: false, timeout: 5000 },
          )
        }
      }).catch(() => {})
    }
  }, [])

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* ══════════════════ SEASONAL BANNER ══════════════════ */}
      {showBanner && (
        <div className="bg-gradient-to-r from-primary to-blue-600 px-4 py-3 text-center text-sm text-white">
          <div className="mx-auto flex max-w-5xl flex-wrap items-center justify-center gap-x-3 gap-y-2">
            <span><span className="mr-1" aria-hidden="true">☀️</span> Été 2026 — Préparez votre climatisation !</span>
            <Link
              to="/prix/climaticien-nice"
              className="inline-flex min-h-11 items-center gap-1.5 rounded-xl bg-white px-4 py-2 text-sm font-semibold text-primary shadow-sm transition hover:bg-white/95 hover:shadow"
            >
              Devis gratuit <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
            <button
              type="button"
              onClick={() => setShowBanner(false)}
              aria-label="Fermer la bannière"
              className="ml-1 inline-flex min-h-11 min-w-11 items-center justify-center rounded-md text-base text-white hover:bg-white/15"
            >
              <span aria-hidden="true">✕</span>
            </button>
          </div>
        </div>
      )}

      {/* Resume abandoned devis */}
      {savedDevis && (
        <div className="border-b border-amber-200 bg-amber-50">
          <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-2.5 lg:px-6">
            <p className="text-sm text-amber-800">
              Vous avez une demande en cours — <a href={savedDevis} className="font-semibold underline hover:text-amber-900">reprendre</a>
            </p>
            <button type="button" onClick={() => { setSavedDevis(null); try { sessionStorage.removeItem('devis_progress') } catch {} }} className="text-xs text-amber-600 hover:text-amber-800">✕</button>
          </div>
        </div>
      )}

      {/* Organization (LocalBusiness for SAB) — anchors brand entity. */}
      <StructuredData data={{
        '@context': 'https://schema.org',
        '@type': 'LocalBusiness',
        '@id': 'https://www.afficheservices.fr/#organization',
        name: 'AfficheServices',
        url: 'https://www.afficheservices.fr',
        logo: 'https://www.afficheservices.fr/og-image.png',
        image: ['https://www.afficheservices.fr/og-image.png'],
        description: 'Plateforme de mise en relation entre particuliers et artisans vérifiés en France. Devis gratuits sous 48h.',
        foundingDate: '2024',
        slogan: 'Trouvez le bon artisan près de chez vous',
        priceRange: '€€',
        email: 'contact@afficheservices.fr',
        areaServed: { '@type': 'Country', name: 'France' },
        openingHoursSpecification: [{
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
          opens: '08:00',
          closes: '20:00',
        }],
        contactPoint: [{
          '@type': 'ContactPoint',
          email: 'contact@afficheservices.fr',
          contactType: 'customer service',
          availableLanguage: 'French',
          areaServed: 'FR',
        }],
        sameAs: [
          'https://www.facebook.com/afficheservices',
          'https://www.linkedin.com/company/afficheservices',
          'https://twitter.com/afficheservices',
        ],
        knowsAbout: [
          'Travaux de rénovation',
          'Plomberie',
          'Électricité',
          'Peinture',
          'Isolation thermique',
          'Toiture',
          'Climatisation',
          'Menuiserie',
          'Carrelage',
          'Déménagement',
        ],
        numberOfEmployees: { '@type': 'QuantitativeValue', value: 5 },
      }} />
      <StructuredData data={{
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: 'AfficheServices — Trouvez le bon artisan près de chez vous',
        description: 'Comparez les devis de professionnels vérifiés pour vos travaux. Service 100% gratuit.',
        url: 'https://www.afficheservices.fr',
        mainEntity: {
          '@type': 'ItemList',
          numberOfItems: categories.length,
          itemListElement: categories.slice(0, 8).map((cat, i) => ({
            '@type': 'ListItem',
            position: i + 1,
            name: cat.name,
            url: `https://www.afficheservices.fr/services/${cat.slug}`,
          })),
        },
      }} />

      {/* ══════════════════ HERO ══════════════════ */}
      <section className="relative overflow-hidden bg-white border-b border-slate-100">
        {/* Subtle decorative elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
          <div className="absolute -right-16 top-10 h-72 w-72 rounded-full bg-primary/[0.03] blur-2xl" />
          <div className="absolute -left-16 bottom-10 h-56 w-56 rounded-full bg-emerald-500/[0.03] blur-2xl" />
          {/* Grid pattern */}
          <div className="absolute inset-0 opacity-[0.015]" style={{ backgroundImage: 'radial-gradient(circle, #2563eb 1px, transparent 1px)', backgroundSize: '32px 32px' }} />
        </div>

        {/* Floating tool icons — desktop only */}
        <div className="absolute inset-0 hidden lg:block pointer-events-none" aria-hidden="true">
          <div className="absolute left-[8%] top-[25%] flex h-12 w-12 items-center justify-center rounded-xl bg-blue-50 shadow-sm rotate-[-8deg] animate-float">
            <Wrench className="h-6 w-6 text-blue-400" />
          </div>
          <div className="absolute right-[10%] top-[20%] flex h-10 w-10 items-center justify-center rounded-xl bg-amber-50 shadow-sm rotate-[12deg] animate-float-delayed">
            <Paintbrush className="h-5 w-5 text-amber-400" />
          </div>
          <div className="absolute left-[12%] bottom-[20%] flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-50 shadow-sm rotate-[6deg] animate-float">
            <Zap className="h-5 w-5 text-emerald-400" />
          </div>
          <div className="absolute right-[8%] bottom-[25%] flex h-10 w-10 items-center justify-center rounded-xl bg-rose-50 shadow-sm rotate-[-5deg] animate-float-delayed">
            <Home className="h-5 w-5 text-rose-400" />
          </div>
        </div>

        <div className="relative mx-auto max-w-7xl px-4 py-14 lg:px-6 lg:py-24">
          <div className="mx-auto max-w-3xl text-center">
            {/* Badge — show real activity instead of "new platform" (anti-trust) */}
            <div className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-4 py-1.5 text-xs font-medium text-emerald-700">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
              </span>
              {totalLeadCount > 0
                ? <><AnimatedNumber value={totalLeadCount} /> demandes de devis · Artisans 100% vérifiés</>
                : 'Artisans vérifiés · Devis gratuits sous 48h'}
            </div>

            <h1 className="mt-6 text-4xl font-extrabold leading-[1.1] text-slate-900 sm:text-5xl lg:text-6xl">
              {userCity ? (
                <>Artisans vérifiés à{' '}<span className="text-primary">{userCity}</span></>
              ) : (
                <>Trouvez le bon artisan{' '}<span className="text-primary">près de chez vous</span></>
              )}
            </h1>
            <p className="mx-auto mt-5 max-w-xl text-base leading-relaxed text-slate-500 sm:text-lg">
              {userCity
                ? `${totalProsDisplay.toLocaleString('fr-FR')} artisans SIRET-vérifiés à ${userCity}. Filtre RGE pour MaPrimeRénov', 3 devis comparatifs gratuits sous 48h, sans engagement.`
                : `${totalProsDisplay.toLocaleString('fr-FR')} artisans SIRET-vérifiés dans ${citiesCount.toLocaleString('fr-FR')}+ villes. Filtre RGE pour MaPrimeRénov', 3 devis gratuits sous 48h, sans engagement.`}
            </p>
            {totalLeadCount >= 100 && (
              <p className="mx-auto mt-2 text-sm font-semibold text-primary">
                <AnimatedNumber value={totalLeadCount} /> demandes de devis déjà envoyées
              </p>
            )}
            {userCity && (
              <div className="mt-3 flex items-center justify-center gap-2 animate-fade-in">
                <span className="text-xs text-slate-400">
                  Vous semblez être près de {userCity} ?
                </span>
                <Link to={`/ville/${userCity.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                  className="text-xs font-medium text-primary hover:underline">
                  Voir les artisans
                </Link>
              </div>
            )}

            {/* Search bar — centered, prominent. max-w-2xl so the service +
                city + submit fit on one desktop row without truncating the
                service placeholder. */}
            <div className="mx-auto mt-8 max-w-2xl">
              <SearchAutocomplete />
            </div>

            {/* Micro-stats row (single consolidated trust line — the old
                LiveStatsHero duplicated these exact three signals). */}
            <div className="mt-5 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-xs text-slate-500">
              <span>✓ {totalProsDisplay.toLocaleString('fr-FR')}+ artisans vérifiés</span>
              {totalReviews > 0 && (
                <>
                  <span className="hidden sm:inline">·</span>
                  <span>
                    ✓ <strong className="font-semibold text-amber-600">{avgRating.toFixed(1).replace('.', ',')}/5</strong>{' '}
                    sur {totalReviews.toLocaleString('fr-FR')} avis vérifiés
                  </span>
                </>
              )}
              <span className="hidden sm:inline">·</span>
              <span>✓ Réponse sous 48h</span>
              <span className="hidden sm:inline">·</span>
              <span>✓ 100% gratuit</span>
              <span className="hidden sm:inline">·</span>
              <span>✓ Alpes-Maritimes, PACA &amp; toute la France</span>
            </div>

            {/* City chips — Alpes-Maritimes focus */}
            <div className="mt-4 flex flex-wrap justify-center gap-2">
              {CITY_CHIPS.map(city => (
                <Link
                  key={city}
                  to={`/travaux/${city.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-')}`}
                  className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs text-slate-600 hover:border-primary hover:text-primary transition-all"
                >
                  📍 {city}
                </Link>
              ))}
            </div>

            {/* Service pills removed — they duplicated the search field's
                category dropdown. The autocomplete in SearchAutocomplete
                already exposes the same categories with better filtering. */}
          </div>
        </div>
      </section>

      {/* ══════════════════ LIVE DEVIS COUNTER ══════════════════ */}
      <div className="border-b border-slate-100 bg-slate-50 px-4 py-3 text-center text-sm text-slate-700">
        <span className="inline-flex items-center gap-2">
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-75" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-red-500" />
          </span>
          {todayLeadCount > 0 ? (
            <><span className="font-semibold text-slate-900"><AnimatedNumber value={todayLeadCount} /></span> demandes de devis aujourd'hui</>
          ) : (
            <>Réseau d'artisans actif aujourd'hui</>
          )}
          {totalLeadCount >= 100 && (
            <>
              <span className="text-slate-300">·</span>
              <span className="font-semibold text-slate-900"><AnimatedNumber value={totalLeadCount} /></span> demandes au total
            </>
          )}
        </span>
      </div>

      {/* ══════════════════ QUICK PRICE ESTIMATOR ══════════════════ */}
      <QuickPriceEstimator />

      {/* ══════════════════ CATEGORY BAR (scrollable with fade hints) ══════════════════ */}
      <section className="border-b border-border bg-white">
        <div className="mx-auto max-w-7xl px-4 py-6 lg:px-6">
          <div className="relative">
            {/* Fade hint right */}
            <div className="pointer-events-none absolute right-0 top-0 z-10 h-full w-16 bg-gradient-to-l from-white to-transparent" />
            <div className="flex items-center gap-5 overflow-x-auto pb-2 scrollbar-hide">
              {categories.slice(0, 12).map((cat) => {
                const Icon = ICON_MAP[cat.slug] ?? Wrench
                const color = ICON_COLORS[cat.slug] ?? 'bg-slate-100 text-slate-600 group-hover:bg-primary'
                return (
                  <Link
                    key={cat.id}
                    to={`/devis-gratuit?service=${cat.slug}`}
                    className="group flex shrink-0 flex-col items-center gap-2"
                  >
                    <div className={`flex h-14 w-14 items-center justify-center rounded-full transition-all group-hover:shadow-md ${color}`}>
                      <Icon className="h-6 w-6 transition-colors group-hover:text-white" />
                    </div>
                    <span className="w-20 truncate text-center text-[11px] font-medium text-slate-600 group-hover:text-primary">{cat.name}</span>
                  </Link>
                )
              })}
              {/* "See all" link at end of scroll */}
              <Link
                to="/services"
                className="flex shrink-0 flex-col items-center gap-2"
              >
                <div className="flex h-14 w-14 items-center justify-center rounded-full border-2 border-dashed border-slate-200 transition-all hover:border-primary hover:bg-primary-50">
                  <ChevronRight className="h-5 w-5 text-slate-400" />
                </div>
                <span className="w-16 text-center text-xs font-medium text-primary">Tout voir</span>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════ POPULAR SEARCHES ══════════════════ */}
      <section className="border-b border-slate-100 bg-white py-8">
        <div className="mx-auto max-w-7xl px-4 lg:px-6">
          <h2 className="text-center text-sm font-semibold text-slate-400 uppercase tracking-wider">Recherches populaires</h2>
          <div className="mt-4 flex flex-wrap justify-center gap-2">
            {POPULAR_SEARCHES.map((s) => (
              <Link
                key={s.label}
                to={s.href}
                className="rounded-full border border-slate-200 bg-white px-4 py-2 text-sm text-slate-600 transition-all hover:border-primary hover:text-primary hover:shadow-sm"
              >
                {s.label}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════ COMMON PROBLEMS (entry into the /problemes mesh) ══════════════════ */}
      <section className="border-b border-slate-100 bg-slate-50/60 py-8">
        <div className="mx-auto max-w-7xl px-4 lg:px-6">
          <h2 className="text-center text-sm font-semibold text-slate-400 uppercase tracking-wider">Problèmes fréquents</h2>
          <div className="mt-4 flex flex-wrap justify-center gap-2">
            {POPULAR_PROBLEMS.map((p) => (
              <Link
                key={p.slug}
                to={`/problemes/${p.slug}`}
                className="rounded-full border border-slate-200 bg-white px-4 py-2 text-sm text-slate-600 transition-all hover:border-primary hover:text-primary hover:shadow-sm"
              >
                {p.label}
              </Link>
            ))}
            <Link
              to="/problemes"
              className="rounded-full border border-primary/30 bg-primary/5 px-4 py-2 text-sm font-semibold text-primary transition-all hover:bg-primary hover:text-white"
            >
              Tous les problèmes <ArrowRight className="inline h-3.5 w-3.5" />
            </Link>
          </div>
        </div>
      </section>

      {/* ══════════════════ POPULAR PROJECTS (Angi-style cards with rating + price) ══════════════════ */}
      <section className="bg-white py-14">
        <div className="mx-auto max-w-7xl px-4 lg:px-6">
          <FadeInUp>
            <h2 className="text-2xl font-bold text-slate-900">
              Services populaires{userCity ? ` à ${userCity}` : ' en France'}
            </h2>
          </FadeInUp>

          <StaggerChildren staggerMs={80} className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {categories.slice(0, 8).map((cat, i) => {
              const Icon = ICON_MAP[cat.slug] ?? Wrench
              const colors = [
                { icon: 'bg-blue-100 text-blue-600', price: 'text-blue-600' },
                { icon: 'bg-amber-100 text-amber-600', price: 'text-amber-600' },
                { icon: 'bg-rose-100 text-rose-600', price: 'text-rose-600' },
                { icon: 'bg-emerald-100 text-emerald-600', price: 'text-emerald-600' },
                { icon: 'bg-violet-100 text-violet-600', price: 'text-violet-600' },
                { icon: 'bg-cyan-100 text-cyan-600', price: 'text-cyan-600' },
                { icon: 'bg-orange-100 text-orange-600', price: 'text-orange-600' },
                { icon: 'bg-teal-100 text-teal-600', price: 'text-teal-600' },
              ]
              const c = colors[i % 8]
              return (
                <Link
                  key={cat.id}
                  to={`/devis-gratuit?service=${cat.slug}`}
                  className="group flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-4 transition-all hover:shadow-md hover:border-primary/30"
                >
                  <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${c.icon}`}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="text-sm font-semibold text-slate-900 group-hover:text-primary">{cat.name}</h3>
                    {SERVICE_PRICES[cat.slug] && (
                      <p className={`mt-0.5 text-xs font-semibold ${c.price}`}>{SERVICE_PRICES[cat.slug]} €</p>
                    )}
                  </div>
                </Link>
              )
            })}
          </StaggerChildren>
        </div>
      </section>

      {/* ══════════════════ LIVE DEMAND FEED ══════════════════ */}
      {recentLeads.length > 0 && (
        <section className="border-t border-border bg-white py-10">
          <div className="mx-auto max-w-7xl px-4 lg:px-6">
            <div className="flex items-center gap-3">
              <div className="relative flex h-2.5 w-2.5">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-500" />
              </div>
              <h2 className="text-lg font-bold text-slate-900">Demandes récentes</h2>
              <span className="rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-700">En direct</span>
            </div>
            <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {recentLeads.map((lead, i) => {
                const urgencyLabels: Record<string, { label: string; color: string }> = {
                  urgent_24h: { label: 'Urgent', color: 'bg-red-50 text-red-600' },
                  within_days: { label: 'Sous quelques jours', color: 'bg-amber-50 text-amber-600' },
                  within_week: { label: 'Cette semaine', color: 'bg-blue-50 text-blue-600' },
                  flexible: { label: 'Flexible', color: 'bg-slate-100 text-slate-500' },
                }
                const urg = urgencyLabels[lead.urgency] ?? urgencyLabels.flexible
                const timeAgo = Math.max(1, Math.round((Date.now() - new Date(lead.createdAt).getTime()) / (1000 * 60 * 60)))
                return (
                  <div key={i} className="flex items-start gap-3 rounded-xl border border-slate-100 bg-slate-50/50 p-4">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-sm font-bold text-primary">
                      {lead.categoryName?.[0] ?? '?'}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-slate-900 truncate">{lead.categoryName ?? 'Service'}</span>
                        <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold ${urg.color}`}>{urg.label}</span>
                      </div>
                      <div className="mt-1 flex items-center gap-2 text-xs text-slate-400">
                        <MapPin className="h-3 w-3" />
                        <span>{lead.city}</span>
                        <span>·</span>
                        <Clock className="h-3 w-3" />
                        <span>il y a {timeAgo}h</span>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
            <div className="mt-5 text-center">
              <Link to="/demande-devis" className="text-sm font-semibold text-primary hover:underline">
                Publier votre projet gratuitement →
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* ══════════════════ TOP SERVICES BY CITY — replace artisans section ══════════════════ */}
      <section className="border-t border-border bg-surface-secondary py-14">
        <div className="mx-auto max-w-7xl px-4 lg:px-6">
          <FadeInUp>
            <h2 className="text-2xl font-bold text-slate-900">Trouvez un artisan dans votre ville</h2>
            <p className="mt-1 text-sm text-slate-500">Les services les plus demandés dans les Alpes-Maritimes et en France</p>
          </FadeInUp>

          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {[
              { city: 'Nice', services: ['Plombier', 'Électricien', 'Climaticien', 'Peintre'], icon: '🏖️' },
              { city: 'Antibes', services: ['Plombier', 'Pisciniste', 'Couvreur', 'Peintre'], icon: '⚓' },
              { city: 'Cannes', services: ['Climaticien', 'Serrurier', 'Électricien', 'Peintre'], icon: '🎬' },
              { city: 'Grasse', services: ['Plombier', 'Maçon', 'Couvreur', 'Jardinier'], icon: '🌸' },
              { city: 'Menton', services: ['Plombier', 'Climaticien', 'Vitrier', 'Façadier'], icon: '🍋' },
              { city: 'Paris', services: ['Plombier', 'Électricien', 'Peintre', 'Serrurier'], icon: '🗼' },
            ].map((item) => {
              const citySlug = item.city.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-')
              return (
                <div key={item.city} className="rounded-2xl border border-border bg-white p-5 shadow-xs hover:border-primary/30 transition-colors">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{item.icon}</span>
                    <div>
                      <h3 className="text-base font-semibold text-slate-900">{item.city}</h3>
                      <p className="text-xs text-slate-400">Artisans vérifiés disponibles</p>
                    </div>
                  </div>
                  <div className="mt-4 flex flex-wrap gap-1.5">
                    {item.services.map((svc) => {
                      const svcSlug = svc.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-')
                      return (
                        <Link key={svc} to={`/prix/${svcSlug}-${citySlug}`}
                          className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs text-slate-600 hover:border-primary hover:bg-primary/5 hover:text-primary transition-colors">
                          {svc}
                        </Link>
                      )
                    })}
                  </div>
                  <Link to={`/travaux/${citySlug}`} className="mt-4 flex items-center gap-1 text-xs font-semibold text-primary hover:underline">
                    Tous les services à {item.city} <ArrowRight className="h-3 w-3" />
                  </Link>
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {/* ══════════════════ HOW IT WORKS ══════════════════ */}
      <section className="bg-white py-14">
        <div className="mx-auto max-w-7xl px-4 lg:px-6">
          <FadeInUp>
          <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
            <div>
              <span className="text-sm font-semibold text-primary">Comment ça marche</span>
              <h2 className="mt-2 text-3xl font-bold text-slate-900">
                Trouvez le bon artisan en 3 étapes
              </h2>
              <div className="mt-8 space-y-6">
                {[
                  { num: '1', icon: FileText, title: 'Décrivez votre projet', desc: 'Remplissez notre formulaire en 2 minutes. Type de travaux, localisation et préférences.', color: 'bg-blue-50 text-blue-600 border-blue-100' },
                  { num: '2', icon: Users, title: 'Recevez des devis', desc: "Jusqu'à 3 artisans vérifiés et assurés vous contactent sous 48h avec leurs devis.", color: 'bg-amber-50 text-amber-600 border-amber-100' },
                  { num: '3', icon: CheckCircle, title: 'Comparez et choisissez', desc: 'Comparez les prix, les avis et les profils. Choisissez le pro qui vous convient.', color: 'bg-emerald-50 text-emerald-600 border-emerald-100' },
                ].map((step) => (
                  <div key={step.num} className={`flex gap-4 rounded-xl border p-4 ${step.color.split(' ')[2]} bg-white`}>
                    <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl ${step.color.split(' ').slice(0, 2).join(' ')}`}>
                      <step.icon className="h-6 w-6" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary text-[10px] font-bold text-white">{step.num}</span>
                        <h3 className="text-base font-semibold text-slate-900">{step.title}</h3>
                      </div>
                      <p className="mt-1 text-sm text-slate-500">{step.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <Link
                to="/demande-devis"
                className="mt-8 inline-flex items-center gap-2 rounded-xl bg-primary px-6 py-3 text-sm font-semibold text-white shadow-sm hover:bg-primary-hover btn-press"
              >
                Commencer maintenant <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
            {/* Trust stats grid */}
            <div className="grid grid-cols-2 gap-4">
              {[
                { icon: Shield, value: '100%', label: 'Artisans vérifiés et assurés', color: 'bg-blue-50 text-blue-600' },
                { icon: Clock, value: '48h', label: 'Délai de réponse garanti', color: 'bg-amber-50 text-amber-600' },
                { icon: Star, value: 'Gratuit', label: 'Service sans engagement', color: 'bg-emerald-50 text-emerald-600' },
                { icon: CheckCircle, value: '3 max', label: 'Devis par demande', color: 'bg-violet-50 text-violet-600' },
              ].map((stat) => (
                <div key={stat.label} className="rounded-2xl border border-border bg-white p-6 shadow-xs">
                  <div className={`inline-flex h-10 w-10 items-center justify-center rounded-lg ${stat.color}`}>
                    <stat.icon className="h-5 w-5" />
                  </div>
                  <p className="mt-3 text-2xl font-bold text-slate-900">{stat.value}</p>
                  <p className="mt-1 text-sm text-slate-500">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
          </FadeInUp>
        </div>
      </section>

      {/* ══════════════════ EXEMPLES DE RÉALISATIONS ══════════════════ */}
      <section className="border-t border-slate-100 bg-white py-14">
        <div className="mx-auto max-w-7xl px-4 lg:px-6">
          <FadeInUp>
            <h2 className="text-2xl font-bold text-slate-900">Exemples de réalisations</h2>
            <p className="mt-1 text-sm text-slate-500">Projets récents réalisés par nos artisans vérifiés</p>
          </FadeInUp>
          <StaggerChildren staggerMs={100} className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {REALISATION_CARDS.map((project) => (
              <div key={project.type} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-xs transition-all hover:shadow-md">
                <div className="flex items-center gap-2">
                  <span className="rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary">{project.type}</span>
                  <span className="flex items-center gap-1 text-xs text-slate-400"><MapPin className="h-3 w-3" /> {project.city}</span>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-slate-600">{project.description}</p>
                <div className="mt-4 flex items-center justify-between">
                  <span className="text-lg font-bold text-slate-900">{project.budget}</span>
                  <Link to={project.href} className="text-xs font-semibold text-primary hover:underline">
                    Voir les prix →
                  </Link>
                </div>
              </div>
            ))}
          </StaggerChildren>
        </div>
      </section>

      {/* ══════════════════ TRUST BADGES ══════════════════ */}
      <section className="border-y border-slate-100 bg-slate-50 py-6">
        <div className="mx-auto flex max-w-5xl flex-wrap items-center justify-center gap-6 px-4 sm:gap-8">
          {[
            { icon: Shield, label: 'SIRET vérifié' },
            { icon: CheckCircle, label: 'Assurance RC Pro' },
            { icon: BadgeCheck, label: 'Garantie décennale' },
            { icon: BadgeCheck, label: 'RGE' },
            { icon: Star, label: 'Avis vérifiés' },
          ].map((badge) => (
            <div key={badge.label} className="flex items-center gap-2 opacity-60">
              <badge.icon className="h-5 w-5 text-slate-500" />
              <span className="text-xs font-medium text-slate-600">{badge.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ══════════════════ TRUST BAR (compact) ══════════════════ */}
      <PartnerLogos />

      {/* ══════════════════ TESTIMONIALS ══════════════════ */}
      {/* Only render when we have at least one real review — never show
          stock testimonials. See loader for the rationale (audit #5). */}
      {testimonials.length > 0 && (
      <section className="bg-white py-14">
        <div className="mx-auto max-w-7xl px-4 lg:px-6">
          <FadeInUp>
          <h2 className="text-2xl font-bold text-slate-900">Ce que disent nos clients</h2>
          </FadeInUp>

          <StaggerChildren staggerMs={150} className="mt-8 grid gap-6 lg:grid-cols-3">
            {testimonials.map((t) => (
              <div key={t.name} className="relative rounded-2xl border border-border bg-white p-6 shadow-xs">
                <Quote className="absolute right-5 top-5 h-8 w-8 text-slate-100" />
                <div className="flex gap-0.5">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Star key={i} className={`h-4 w-4 ${i <= t.rating ? 'fill-amber-400 text-amber-400' : 'text-slate-200'}`} />
                  ))}
                </div>
                <p className="mt-4 text-sm leading-relaxed text-slate-600">&ldquo;{t.text}&rdquo;</p>
                <div className="mt-5 flex items-center gap-3 border-t border-border pt-4">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-sm font-bold text-primary">{t.name[0]}</div>
                  <div>
                    <p className="text-sm font-semibold text-slate-900">{t.name}</p>
                    <p className="text-xs text-slate-400">{t.city} · {t.service}</p>
                  </div>
                </div>
              </div>
            ))}
          </StaggerChildren>
        </div>
      </section>
      )}

      {/* ══════════════════ EXPERT MARKET OVERVIEW — SEO + E-E-A-T ══════════════════ */}
      <section className="border-t border-slate-100 bg-white py-14">
        <div className="mx-auto max-w-4xl px-4 lg:px-6">
          <div className="flex items-center gap-2 text-xs text-slate-400">
            <BadgeCheck className="h-3.5 w-3.5 text-emerald-500" />
            <span>Mis à jour en {lastUpdatedLabel}</span>
            <span>·</span>
            <span>Vérifié par notre équipe éditoriale</span>
          </div>
          <h2 className="mt-3 text-2xl font-bold text-slate-900">Le marché des travaux en France en 2026</h2>
          <div className="mt-6 space-y-4 text-sm leading-relaxed text-slate-600">
            <p>Le secteur du bâtiment en France représente plus de <strong>150 milliards d'euros de chiffre d'affaires annuel</strong>, porté par 620 000 entreprises artisanales. En 2026, la demande en rénovation énergétique atteint un niveau record grâce au renforcement de <strong>MaPrimeRénov'</strong> et à l'obligation d'audit énergétique pour les passoires thermiques (DPE F et G).</p>
            <p>Les métiers les plus demandés restent la <strong>plomberie</strong> (25 % des demandes), l'<strong>électricité</strong> (18 %), la <strong>peinture</strong> (15 %) et la <strong>rénovation générale</strong> (12 %). Les régions Île-de-France, Auvergne-Rhône-Alpes et Provence-Alpes-Côte d'Azur concentrent 45 % de la demande nationale.</p>
            <p>Le prix moyen d'une intervention artisanale en France est de <strong>250-350 € pour un dépannage</strong> et de <strong>800-1 500 €/m² pour une rénovation complète</strong>. Les tarifs varient de +25 % en Île-de-France à -10 % en zones rurales. Comparer au moins 3 devis permet d'économiser en moyenne 20-30 % sur le budget total.</p>
          </div>
          <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {[
              { numValue: 620000, suffix: '', label: 'artisans en France' },
              { numValue: 150, suffix: ' Md€', label: 'CA annuel bâtiment' },
              { numValue: 3, suffix: ' devis', label: '= 20-30% d\'économie' },
              { numValue: 48, suffix: 'h', label: 'délai de réponse' },
            ].map((s) => (
              <div key={s.label} className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-center">
                <p className="text-lg font-bold text-slate-900"><AnimatedNumber value={s.numValue} suffix={s.suffix} /></p>
                <p className="mt-0.5 text-[10px] text-slate-500">{s.label}</p>
              </div>
            ))}
          </div>
          <div className="mt-6 rounded-xl border border-slate-200 bg-slate-50 p-4">
            <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-500">Sources</p>
            <ul className="mt-2 space-y-1 text-xs text-slate-500">
              <li>• <a href="https://www.ffbatiment.fr" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">FFB</a> — Fédération Française du Bâtiment, données 2026</li>
              <li>• <a href="https://france-renov.gouv.fr" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">France Rénov'</a> — Guichet unique MaPrimeRénov'</li>
              <li>• <a href="https://www.ademe.fr" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">ADEME</a> — Agence de la transition écologique</li>
            </ul>
          </div>
        </div>
      </section>

      {/* ══════════════════ HOMEPAGE FAQ — SEO rich snippets ══════════════════ */}
      <section className="border-t border-slate-100 bg-surface-secondary py-14">
        <div className="mx-auto max-w-3xl px-4 lg:px-6">
          <h2 className="text-center text-2xl font-bold text-slate-900">Questions fréquentes</h2>
          <div className="mt-8 space-y-4">
            {[
              { q: 'AfficheServices est-il vraiment gratuit ?', a: 'Oui, le service est 100 % gratuit pour les particuliers. Vous décrivez votre projet, recevez jusqu\'à 3 devis d\'artisans vérifiés, et choisissez librement. Aucun engagement, aucun frais caché.' },
              { q: 'Comment les artisans sont-ils vérifiés ?', a: 'Chaque professionnel est contrôlé sur 4 critères : SIRET actif (vérification INSEE), assurance RC professionnelle en cours, garantie décennale pour les travaux concernés, et identité du gérant. Les artisans dont la note passe sous 3/5 sont suspendus.' },
              { q: 'Combien de devis vais-je recevoir ?', a: 'Vous recevez jusqu\'à 3 devis de professionnels différents, généralement sous 48 heures. Chaque devis est détaillé (main d\'œuvre + fournitures) pour faciliter la comparaison.' },
              { q: 'Quels types de travaux puis-je demander ?', a: 'Tous les travaux du bâtiment : plomberie, électricité, peinture, rénovation, menuiserie, toiture, serrurerie, climatisation, carrelage, maçonnerie, isolation, jardinage, déménagement, et bien plus. Plus de 20 catégories de services sont couvertes.' },
              { q: 'Que faire si le travail n\'est pas satisfaisant ?', a: 'Tous nos artisans sont assurés (RC Pro + garantie décennale). En cas de litige, notre service client vous accompagne dans la résolution. Vous pouvez aussi laisser un avis qui sera visible par tous les futurs clients.' },
              { q: 'Puis-je utiliser MaPrimeRénov\' avec AfficheServices ?', a: 'Oui. De nombreux artisans sur AfficheServices sont certifiés RGE (Reconnu Garant de l\'Environnement), condition nécessaire pour bénéficier de MaPrimeRénov\', des CEE et de l\'éco-PTZ. Vérifiez la qualification RGE sur le profil de l\'artisan.' },
            ].map((faq, i) => (
              <div key={i} className="rounded-xl border border-slate-200 bg-white p-5">
                <h3 className="text-sm font-semibold text-slate-900">{faq.q}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════ PRO CTA — light, in-line with the rest ══════════════════ */}
      <section className="border-t border-border bg-white py-16">
        <div className="mx-auto max-w-7xl px-4 lg:px-6">
          <div className="overflow-hidden rounded-3xl border border-border bg-gradient-to-br from-primary-50 via-white to-white p-8 sm:p-12">
            <div className="flex flex-col gap-10 lg:flex-row lg:items-center lg:justify-between">
              <div className="max-w-lg">
                <span className="text-sm font-semibold text-primary">Espace pro</span>
                <h2 className="mt-2 text-3xl font-bold text-slate-900">Vous êtes artisan ?</h2>
                <p className="mt-4 text-base text-slate-500">
                  Rejoignez notre réseau de professionnels et recevez des demandes de devis qualifiées dans votre zone.
                </p>
                <ul className="mt-6 space-y-3">
                  {['Leads qualifiés dans votre zone', 'Profil vérifié avec avis clients', 'Paiement uniquement au résultat', 'Tableau de bord et statistiques'].map((item) => (
                    <li key={item} className="flex items-center gap-3 text-sm text-slate-600">
                      <CheckCircle className="h-4 w-4 shrink-0 text-emerald-500" /> {item}
                    </li>
                  ))}
                </ul>
                <Link to="/register-pro" className="mt-8 inline-flex items-center gap-2 rounded-xl bg-primary px-6 py-3 text-sm font-semibold text-white shadow-sm hover:bg-primary-hover btn-press">
                  Inscription gratuite <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { value: 'Gratuit', label: 'Inscription sans frais' },
                  { value: '48h', label: 'Délai de réponse garanti' },
                  { value: '3 max', label: 'Pros par demande' },
                  { value: '100%', label: 'Artisans vérifiés' },
                ].map((s) => (
                  <div key={s.label} className="rounded-xl border border-border bg-white p-5 text-center shadow-xs">
                    <p className="text-2xl font-bold text-slate-900">{s.value}</p>
                    <p className="mt-1 text-xs text-slate-500">{s.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  )
}
